'use client'

/**
 * The task row's edit form.
 *
 * Split out of `task-row.tsx` in #311, which was 640 lines with this inlined — 140 over the repo's
 * limit before the file moved anywhere. The split is a precondition of the task slice rather than a
 * consequence of it: `EntrySlice.editForm` cannot name a component that is an unexported function
 * inside the row, which is why #154's prototype left `editForm` null on the richest slice.
 *
 * The 18-prop interface is preserved verbatim. Twelve of those props are six `useState` pairs the
 * row owns and hands down one at a time; collapsing them is a separate change, and doing it here
 * would hide a behaviour change inside a move.
 */

import { useState } from 'react'
import { WarningCircleIcon } from '@phosphor-icons/react'
import { format } from 'date-fns'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import {
  MultiSelectProperty,
  DateField,
  PROPERTY_ICONS,
} from '@/ui/property-fields'
import { TopicSubtitleSelector } from '@/capture'
import { AddTopicDialog, useFindOrCreateTopic } from '@/topics'
import { EditFormActions } from '@/feed/row-chrome'
import { formatDateString } from '@/lib/date'
import { cn } from '@/lib/tailwind'
import type { PersonRef } from '@/document'

export interface TaskEditFormProps {
  editTitle: string
  setEditTitle: (v: string) => void
  editDescription: string
  setEditDescription: (v: string) => void
  editImportant: boolean
  setEditImportant: (v: boolean) => void
  editDueDate: string
  setEditDueDate: (v: string) => void
  editPeopleIds: string[]
  setEditPeopleIds: (v: string[]) => void
  editTopicIds: string[]
  setEditTopicIds: (v: string[]) => void
  selectedPeople: PersonRef[]
  availablePeople: PersonRef[]
  availableTopics: Array<{ id: string; name: string }>
  isSaving: boolean
  onCancel: () => void
  onSave: () => void
}

export function TaskEditForm({
  editTitle,
  setEditTitle,
  editDescription,
  setEditDescription,
  editImportant,
  setEditImportant,
  editDueDate,
  setEditDueDate,
  editPeopleIds,
  setEditPeopleIds,
  editTopicIds,
  setEditTopicIds,
  selectedPeople,
  availablePeople,
  availableTopics,
  isSaving,
  onCancel,
  onSave,
}: TaskEditFormProps) {
  const [showAddTopic, setShowAddTopic] = useState(false)
  const findOrCreateTopic = useFindOrCreateTopic()

  const handleCreateTopic = (name: string) => {
    const topicId = findOrCreateTopic(name, 'task')
    if (topicId && !editTopicIds.includes(topicId)) {
      setEditTopicIds([...editTopicIds, topicId])
    }
  }

  const dueDateObj = editDueDate
    ? new Date(editDueDate + 'T00:00:00')
    : undefined

  const handleDueDateChange = (date: Date | null) => {
    if (date) {
      setEditDueDate(formatDateString(date))
    } else {
      setEditDueDate('')
    }
  }

  return (
    <div className="space-y-2">
      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Title
        </label>
        <Input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          className="h-8 text-sm"
          placeholder="Task title"
        />
      </div>

      <div>
        <label className="text-muted-foreground mb-1 block text-xs">
          Description
        </label>
        <Textarea
          value={editDescription}
          onChange={(e) => setEditDescription(e.target.value)}
          className="min-h-[60px] resize-none text-sm"
          placeholder="Add a description..."
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Important Toggle */}
        <button
          type="button"
          onClick={() => setEditImportant(!editImportant)}
          disabled={isSaving}
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-colors sm:h-8',
            editImportant
              ? 'bg-destructive/20 text-destructive'
              : 'border-muted-foreground/40 text-muted-foreground hover:border-muted-foreground/60 hover:text-foreground border border-dashed',
            isSaving && 'cursor-not-allowed opacity-50',
          )}
        >
          <WarningCircleIcon
            className="h-3.5 w-3.5"
            weight={editImportant ? 'fill' : 'regular'}
          />
          {editImportant ? 'Important' : 'Mark important'}
        </button>

        <TopicSubtitleSelector
          selectedTopicIds={editTopicIds}
          availableTopics={availableTopics}
          onChange={setEditTopicIds}
          onAddNew={() => setShowAddTopic(true)}
          onCreateTopic={handleCreateTopic}
          disabled={isSaving}
        />

        <DateField
          value={dueDateObj}
          onChange={handleDueDateChange}
          disabled={isSaving}
          formatDate={(d) => format(d, 'MMM d')}
          placeholder="+ due"
          clearable={!!dueDateObj}
          variant="inline"
        />

        <MultiSelectProperty
          label="People"
          Icon={PROPERTY_ICONS.people}
          selectedItems={selectedPeople}
          availableItems={availablePeople}
          onAdd={(person) => setEditPeopleIds([...editPeopleIds, person.id])}
          onRemove={(person) =>
            setEditPeopleIds(editPeopleIds.filter((id) => id !== person.id))
          }
          getItemLabel={(p) => p.name}
        />
      </div>

      <EditFormActions
        isSaving={isSaving}
        onCancel={onCancel}
        onSave={onSave}
        disabled={!editTitle.trim()}
      />

      <AddTopicDialog
        open={showAddTopic}
        onOpenChange={setShowAddTopic}
        onCreate={handleCreateTopic}
      />
    </div>
  )
}
